import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '../lib/supabase';
import { logger } from '../lib/logger';
import { setSentryUser, Sentry } from '../lib/sentry';

// Profile interface matching the database schema
export interface UserProfile {
  id: string;
  user_id: string;
  name?: string;
  last_name?: string;
  email?: string;
  phone_sms?: string;
  phone_whatsapp?: string;
  is_parent?: boolean;
  admin_level?: string;
  admin_permissions?: string[];
  admin_is_active?: boolean;
  password_change_required?: boolean;
  timezone?: string;
  preferred_language?: string;
  city?: string;
  always_record_sessions?: boolean;
  onboarding_complete?: boolean;
  welcome_token?: string;
  created_at?: string;
  updated_at?: string;
}

export type UserRole = 'admin' | 'scanning_agent' | 'parent' | 'guest';

interface AuthContextType {
  user: User | null;
  session: Session | null;
  profile: UserProfile | null;
  loading: boolean;
  profileLoaded: boolean; // True when profile fetch completed (even if null)
  error: string | null;
  
  // Auth methods
  signIn: (email: string, password: string) => Promise<{ success: boolean; error?: string }>;
  signUp: (email: string, password: string, name: string) => Promise<{ success: boolean; error?: string }>;
  signOut: () => Promise<void>;
  
  // Role helpers
  isAdmin: () => boolean;
  isScanningAgent: () => boolean;
  isParent: () => boolean;
  getRole: () => UserRole;
  hasPermission: (permission: string) => boolean;
  
  // Profile methods
  refetchProfile: () => Promise<void>;
  updatePassword: (newPassword: string) => Promise<{ success: boolean; error?: string }>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: React.ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [profileLoaded, setProfileLoaded] = useState(false); // Track if profile fetch completed
  const [error, setError] = useState<string | null>(null);
  
  // Ref to prevent duplicate profile fetches (race condition guard)
  const fetchingProfileRef = useRef(false);
  const lastFetchedUserIdRef = useRef<string | null>(null);

  // Fetch user profile from database
  const fetchProfile = useCallback(async (userId: string) => {
    // Prevent duplicate fetches for the same user
    if (fetchingProfileRef.current && lastFetchedUserIdRef.current === userId) {
      return;
    }
    
    fetchingProfileRef.current = true;
    lastFetchedUserIdRef.current = userId;
    setProfileLoaded(false); // Reset profile loaded state
    
    try {
      logger.debug('Fetching profile for user:', userId);
      
      // Simple query - get any profile for this user
      // Type assertion: onboarding_complete + welcome_token are in DB but not yet in auto-generated types
      const { data: profile, error: profileError } = await supabase
        .from('profiles')
        .select('id, user_id, name, last_name, email, phone_sms, phone_whatsapp, is_parent, admin_level, admin_permissions, admin_is_active, password_change_required, timezone, preferred_language, city, always_record_sessions, onboarding_complete, welcome_token, created_at, updated_at')
        .eq('user_id', userId)
        .maybeSingle() as unknown as { data: UserProfile | null; error: any };

      if (profileError) {
        if (profileError.message?.includes('AbortError') || profileError.message?.includes('aborted')) {
          return;
        }
        logger.error('Error fetching profile:', profileError);
        Sentry.captureException(new Error(profileError.message), { tags: { flow: 'auth', step: 'fetch_profile' } });
        setProfile(null);
        return;
      }

      if (profile) {
        logger.debug('Profile found:', {
          admin_level: profile.admin_level,
          admin_is_active: profile.admin_is_active,
          is_parent: profile.is_parent
        });
        // Cast profile as UserProfile (DB types use null, our interface uses undefined for optionals)
        setProfile(profile as unknown as UserProfile);
        setSentryUser(profile.id);
        Sentry.addBreadcrumb({
          category: 'auth',
          message: 'Parent logged in',
          level: 'info',
          data: {
            admin_level: profile.admin_level || 'none',
            is_parent: profile.is_parent,
          },
        });
        return;
      }

      // No profile found - attempt to create one for orphaned users
      // This handles cases where the trigger didn't fire (e.g., users created before trigger existed)
      logger.debug('No profile found for user, attempting to create:', userId);
      
      // Get user metadata for name
      const { data: { user: authUser } } = await supabase.auth.getUser();
      const userName = authUser?.user_metadata?.name || '';
      
      const { data: newProfile, error: createError } = await supabase
        .from('profiles')
        .insert({
          user_id: userId,
          name: userName,
          is_parent: true, // Default new users to parent role
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        })
        .select()
        .single();
      
      if (createError) {
        // Could be RLS blocking or duplicate key - either way, no profile access
        logger.error('Failed to create profile for orphaned user:', createError);
        setProfile(null);
        return;
      }
      
      if (newProfile) {
        logger.debug('Created profile for orphaned user:', newProfile.id);
        setProfile(newProfile as unknown as UserProfile);
        return;
      }
      
      // Fallback - no profile access
      setProfile(null);
    } catch (err) {
      logger.error('Error fetching profile:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch profile');
      setProfile(null); // Ensure profile is set to null on error
    } finally {
      fetchingProfileRef.current = false;
      setProfileLoaded(true); // Mark profile loading as complete
    }
  }, []);

  // Initialize auth state
  useEffect(() => {
    let isMounted = true;

    // Set up auth state listener
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        if (!isMounted) return;

        logger.debug('Auth state change:', event, session?.user?.id);
        
        // Handle token refresh failures explicitly.
        // When a refresh token is invalid/expired, Supabase fires TOKEN_REFRESHED
        // with a null session or SIGNED_OUT. Clear stale state and force re-login
        // so users don't get stuck with broken sessions silently.
        if (event === 'TOKEN_REFRESHED' && !session) {
          logger.warn('Token refresh failed — clearing stale session');
          setSession(null);
          setUser(null);
          setProfile(null);
          setProfileLoaded(true);
          setLoading(false);
          return;
        }

        setSession(session);
        setUser(session?.user ?? null);

        if (session?.user) {
          // Defer profile fetch to prevent deadlocks
          setTimeout(() => {
            if (isMounted) {
              fetchProfile(session.user.id);
            }
          }, 0);
        } else {
          setProfile(null);
          setProfileLoaded(true); // No user = no profile to load
        }
        
        setLoading(false);
      }
    );

    // Check for existing session
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (!isMounted) return;

      setSession(session);
      setUser(session?.user ?? null);

      if (session?.user) {
        fetchProfile(session.user.id);
      } else {
        setProfileLoaded(true); // No user = no profile to load
      }
      setLoading(false);
    });

    return () => {
      isMounted = false;
      subscription.unsubscribe();
    };
  }, [fetchProfile]);

  // Map Supabase auth error messages to user-friendly messages
  const mapAuthError = (message: string): string => {
    const lower = message.toLowerCase();
    if (lower.includes('invalid login credentials') || lower.includes('invalid_credentials')) {
      return 'Invalid email or password. Please try again.';
    }
    if (lower.includes('email not confirmed')) {
      return 'Please check your email and confirm your account before logging in.';
    }
    if (lower.includes('too many requests') || lower.includes('rate limit')) {
      return 'Too many attempts. Please wait a moment and try again.';
    }
    if (lower.includes('user already registered') || lower.includes('already been registered')) {
      return 'An account with this email already exists. Please log in instead.';
    }
    if (lower.includes('password') && lower.includes('least')) {
      return 'Password must be at least 6 characters.';
    }
    if (lower.includes('network') || lower.includes('fetch')) {
      return 'Network error. Please check your connection and try again.';
    }
    return 'An error occurred. Please try again.';
  };

  // Sign in with email/password
  const signIn = async (email: string, password: string): Promise<{ success: boolean; error?: string }> => {
    try {
      setError(null);
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) {
        const friendly = mapAuthError(error.message);
        setError(friendly);
        return { success: false, error: friendly };
      }

      return { success: true };
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Sign in failed';
      const friendly = mapAuthError(message);
      Sentry.captureException(err, { tags: { flow: 'auth', step: 'sign_in' } });
      setError(friendly);
      return { success: false, error: friendly };
    }
  };

  // Sign up with email/password
  const signUp = async (email: string, password: string, name: string): Promise<{ success: boolean; error?: string }> => {
    try {
      setError(null);
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: { name }
        }
      });

      if (error) {
        const friendly = mapAuthError(error.message);
        setError(friendly);
        return { success: false, error: friendly };
      }

      return { success: true };
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Sign up failed';
      Sentry.captureException(err, { tags: { flow: 'auth', step: 'sign_up' } });
      setError(message);
      return { success: false, error: message };
    }
  };

  // Sign out
  const signOut = async () => {
    try {
      // Clear local state first
      setUser(null);
      setSession(null);
      setProfile(null);
      setSentryUser(null);
      
      // Sign out from Supabase
      await supabase.auth.signOut({ scope: 'global' });
      
      // Clear any stored data
      localStorage.removeItem('sb-session');
    } catch (err) {
      logger.error('Error signing out:', err);
      Sentry.captureException(err, { tags: { flow: 'auth', step: 'sign_out' } });
    }
  };

  // Update password
  const updatePassword = async (newPassword: string): Promise<{ success: boolean; error?: string }> => {
    try {
      const { error } = await supabase.auth.updateUser({
        password: newPassword
      });

      if (error) {
        return { success: false, error: error.message };
      }

      // Update profile to clear password_change_required flag
      if (user) {
        await supabase
          .from('profiles')
          .update({ password_change_required: false })
          .eq('user_id', user.id);
        
        // Refetch profile
        await fetchProfile(user.id);
      }

      return { success: true };
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Password update failed';
      return { success: false, error: message };
    }
  };

  // Role checking helpers
  
  /**
   * Check if user is a scanning agent.
   * Scanning agents have admin_level = 'scanning_agent' but are NOT admins.
   * They can only access the scanning interface, not the full admin dashboard.
   */
  const isScanningAgent = (): boolean => {
    return profile?.admin_level === 'scanning_agent' && profile?.admin_is_active !== false;
  };

  /**
   * Check if user is an admin.
   * EXCLUDES scanning_agent - they are a separate role with limited access.
   * Admins have full access to the admin dashboard.
   */
  const isAdmin = (): boolean => {
    return !!profile?.admin_level && 
           profile?.admin_level !== 'scanning_agent' && 
           profile?.admin_is_active !== false;
  };

  const isParent = (): boolean => {
    return profile?.is_parent === true;
  };

  const getRole = (): UserRole => {
    if (isAdmin()) return 'admin';
    if (isScanningAgent()) return 'scanning_agent';
    if (isParent()) return 'parent';
    return 'guest';
  };

  const hasPermission = (permission: string): boolean => {
    if (!profile?.admin_permissions) return false;
    return profile.admin_permissions.includes(permission) || 
           profile.admin_permissions.includes('all_access');
  };

  const refetchProfile = async () => {
    if (user) {
      await fetchProfile(user.id);
    }
  };

  const value: AuthContextType = {
    user,
    session,
    profile,
    loading,
    profileLoaded,
    error,
    signIn,
    signUp,
    signOut,
    isAdmin,
    isScanningAgent,
    isParent,
    getRole,
    hasPermission,
    refetchProfile,
    updatePassword,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthProvider;
